// src/pages/admin/AdminTimesheetApprovals.jsx
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { UserContext } from "../../contexts/UserContext";
import {
  formatNotificationCount,
  isAccountantApprover,
  isAdminStaffSubmission,
  isLeaveSubmission,
  isTimesheetSubmission,
} from "../../utils/timesheetNotifications";

const statusBadgeClass = (status) => {
  const clean = String(status || "draft").toLowerCase();
  if (clean === "approved") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (clean === "rejected") return "border-red-200 bg-red-50 text-red-700";
  if (clean === "submitted" || clean === "pending") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-gray-200 bg-gray-50 text-gray-700";
};

const formatHours = (hours) => Number(hours || 0).toFixed(2);

const formatDisplayDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

const formatTimestamp = (value) => {
  if (!value) return "";

  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : typeof value === "number"
        ? new Date(value)
        : value?.seconds
          ? new Date(value.seconds * 1000)
          : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getLeaveSubmittedText = (item) =>
  item.submittedAtText || formatTimestamp(item.submittedAt || item.createdAt);

function PrintableButtons({ onPrint }) {
  return (
    <button
      type="button"
      onClick={onPrint}
      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
    >
      PDF
    </button>
  );
}

function NotificationPill({ count, active = false }) {
  const label = formatNotificationCount(count);
  if (!label) return null;

  return (
    <span
      className={`ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold leading-none ${
        active ? "bg-white text-gray-900" : "bg-red-600 text-white"
      }`}
      aria-label={`${label} notifications`}
    >
      {label}
    </span>
  );
}

export default function AdminTimesheetApprovals() {
  const admin = getAuth().currentUser;
  const { userInfo } = useContext(UserContext) || {};
  const canSeeAdminStaffSubmissions = isAccountantApprover(userInfo);
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("timesheets");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("needsApproval");
  const [notes, setNotes] = useState({});
  const [message, setMessage] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      let timesheetDocs = [];
      try {
        const timesheetSnap = await getDocs(query(collection(db, "timesheets"), orderBy("weekStart", "desc")));
        timesheetDocs = timesheetSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
      } catch (error) {
        console.warn("Timesheet ordered query failed. Falling back to unordered query.", error);
        const timesheetSnap = await getDocs(collection(db, "timesheets"));
        timesheetDocs = timesheetSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
      }

      let leaveDocs = [];
      try {
        const leaveSnap = await getDocs(query(collection(db, "leaveRequests"), orderBy("startDate", "desc")));
        leaveDocs = leaveSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
      } catch (error) {
        console.warn("Leave ordered query failed. Falling back to unordered query.", error);
        const leaveSnap = await getDocs(collection(db, "leaveRequests"));
        leaveDocs = leaveSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
      }

      const visibleTimesheetDocs = canSeeAdminStaffSubmissions
        ? timesheetDocs
        : timesheetDocs.filter((item) => !isAdminStaffSubmission(item));
      const visibleLeaveDocs = canSeeAdminStaffSubmissions
        ? leaveDocs
        : leaveDocs.filter((item) => !isAdminStaffSubmission(item));

      setTimesheets(visibleTimesheetDocs.sort((a, b) => String(b.weekStart || "").localeCompare(String(a.weekStart || ""))));
      setLeaveRequests(visibleLeaveDocs.sort((a, b) => String(b.startDate || "").localeCompare(String(a.startDate || ""))));
    } catch (error) {
      console.error("Failed to load approval records:", error);
      setMessage("Could not load timesheets and leave requests.");
    } finally {
      setLoading(false);
    }
  }, [canSeeAdminStaffSubmissions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filterRecords = useCallback((records, kind) => {
    const term = search.trim().toLowerCase();
    return records.filter((item) => {
      const status = String(item.status || "").toLowerCase();
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "needsApproval" &&
          (kind === "timesheets" ? isTimesheetSubmission(item) : isLeaveSubmission(item))) ||
        status === statusFilter;

      const leaveSubmittedText = getLeaveSubmittedText(item);
      const haystack = kind === "timesheets"
        ? [item.driverName, item.driverEmail, item.weekStart, item.weekEnd, item.status, item.totalHours].filter(Boolean).join(" ").toLowerCase()
        : [item.driverName, item.driverEmail, item.type, item.startDate, item.endDate, item.status, item.reason, leaveSubmittedText].filter(Boolean).join(" ").toLowerCase();

      return matchesStatus && (!term || haystack.includes(term));
    });
  }, [search, statusFilter]);

  const visibleTimesheets = useMemo(
    () => filterRecords(timesheets, "timesheets"),
    [filterRecords, timesheets],
  );
  const visibleLeaves = useMemo(
    () => filterRecords(leaveRequests, "leave"),
    [filterRecords, leaveRequests],
  );
  const timesheetSubmissionCount = useMemo(
    () => timesheets.filter(isTimesheetSubmission).length,
    [timesheets],
  );
  const leaveSubmissionCount = useMemo(
    () => leaveRequests.filter(isLeaveSubmission).length,
    [leaveRequests],
  );
  const needsApprovalCount = timesheetSubmissionCount + leaveSubmissionCount;

  const approveOrRejectTimesheet = async (item, nextStatus) => {
    try {
      await updateDoc(doc(db, "timesheets", item.id), {
        status: nextStatus,
        managerNote: notes[item.id] || item.managerNote || "",
        approvedBy: nextStatus === "approved" ? admin?.email || "admin" : null,
        rejectedBy: nextStatus === "rejected" ? admin?.email || "admin" : null,
        approvedAt: nextStatus === "approved" ? serverTimestamp() : null,
        rejectedAt: nextStatus === "rejected" ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        readyForFinalPdfEmail: nextStatus === "approved",
      });
      setMessage(nextStatus === "approved" ? "Timesheet approved." : "Timesheet rejected.");
      loadData();
    } catch (error) {
      console.error("Failed to update timesheet:", error);
      setMessage("Could not update this timesheet.");
    }
  };

  const approveOrRejectLeave = async (item, nextStatus) => {
    try {
      await updateDoc(doc(db, "leaveRequests", item.id), {
        status: nextStatus,
        managerNote: notes[item.id] || item.managerNote || "",
        approvedBy: nextStatus === "approved" ? admin?.email || "admin" : null,
        rejectedBy: nextStatus === "rejected" ? admin?.email || "admin" : null,
        approvedAt: nextStatus === "approved" ? serverTimestamp() : null,
        rejectedAt: nextStatus === "rejected" ? serverTimestamp() : null,
        decisionUpdatedAt: serverTimestamp(),
        driverDecisionSeenAt: null,
        updatedAt: serverTimestamp(),
        readyForFinalPdfEmail: nextStatus === "approved",
      });
      setMessage(nextStatus === "approved" ? "Leave request approved." : "Leave request rejected.");
      loadData();
    } catch (error) {
      console.error("Failed to update leave request:", error);
      setMessage("Could not update this leave request.");
    }
  };

  const openPrintableTimesheet = (item) => {
    const rowsHtml = (Array.isArray(item.days) ? item.days : [])
      .map(
        (day) => `
          <tr>
            <td>${formatDisplayDate(day.date)}<br/><small>${day.date || ""}</small></td>
            <td>${day.startTime || "-"}</td>
            <td>${day.finishTime || "-"}</td>
            <td>${day.breakMinutes || "0"}</td>
            <td>${day.secondStartTime || "-"}</td>
            <td>${day.secondFinishTime || "-"}</td>
            <td>${day.secondBreakMinutes || "0"}</td>
            <td>${formatHours(day.totalHours)}</td>
            <td>${day.notes || ""}</td>
          </tr>
        `,
      )
      .join("");

    const signatureHtml = item.signature
      ? `<img src="${item.signature}" alt="Driver signature" style="max-width: 280px; max-height: 90px; border: 1px solid #ddd; border-radius: 8px;" />`
      : "No signature";

    const popup = window.open("", "_blank", "width=1000,height=800");
    popup.document.write(`
      <html>
        <head>
          <title>Timesheet ${item.weekStart || ""}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; padding: 28px; }
            h1 { margin: 0 0 4px; font-size: 24px; }
            .muted { color: #6b7280; font-size: 13px; }
            .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin: 18px 0; }
            .box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 14px; }
            th, td { border: 1px solid #e5e7eb; padding: 9px; text-align: left; vertical-align: top; font-size: 13px; }
            th { background: #f3f4f6; }
            .footer { margin-top: 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
            @media print { button { display: none; } body { padding: 0; } }
          </style>
        </head>
        <body>
          <button onclick="window.print()" style="margin-bottom:16px;padding:10px 14px;border-radius:10px;border:1px solid #ddd;background:#111827;color:#fff;font-weight:700;">Print / Save PDF</button>
          <h1>Greenspace Industries Timesheet</h1>
          <div class="muted">Manager approval copy</div>
          <div class="meta">
            <div class="box"><strong>Driver</strong><br/>${item.driverName || "Driver"}<br/><span class="muted">${item.driverEmail || ""}</span></div>
            <div class="box"><strong>Week</strong><br/>${item.weekStart || ""} to ${item.weekEnd || ""}<br/><span class="muted">Status: ${item.status || "draft"}</span></div>
            <div class="box"><strong>Total hours</strong><br/>${formatHours(item.totalHours)}</div>
            <div class="box"><strong>Manager note</strong><br/>${item.managerNote || ""}</div>
          </div>
          <table>
            <thead><tr><th>Day</th><th>Start</th><th>Finish</th><th>Break</th><th>Start</th><th>Finish</th><th>Break</th><th>Hours</th><th>Notes</th></tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <div class="footer">
            <div class="box"><strong>Driver signature</strong><br/><br/>${signatureHtml}</div>
            <div class="box"><strong>Approved by</strong><br/><br/>${item.approvedBy || ""}</div>
          </div>
        </body>
      </html>
    `);
    popup.document.close();
  };

  const openPrintableLeave = (item) => {
    const signatureHtml = item.signature
      ? `<img src="${item.signature}" alt="Driver signature" style="max-width: 280px; max-height: 90px; border: 1px solid #ddd; border-radius: 8px;" />`
      : "No signature";
    const submittedText = getLeaveSubmittedText(item) || "Not available";
    const popup = window.open("", "_blank", "width=900,height=700");
    popup.document.write(`
      <html>
        <head>
          <title>Leave Request ${item.startDate || ""}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; padding: 28px; }
            .box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin: 12px 0; }
            .muted { color: #6b7280; font-size: 13px; }
            @media print { button { display: none; } body { padding: 0; } }
          </style>
        </head>
        <body>
          <button onclick="window.print()" style="margin-bottom:16px;padding:10px 14px;border-radius:10px;border:1px solid #ddd;background:#111827;color:#fff;font-weight:700;">Print / Save PDF</button>
          <h1>Greenspace Industries Leave Request</h1>
          <div class="box"><strong>Driver</strong><br/>${item.driverName || "Driver"}<br/><span class="muted">${item.driverEmail || ""}</span></div>
          <div class="box"><strong>Type</strong><br/>${item.type || ""}</div>
          <div class="box"><strong>Dates</strong><br/>${item.startDate || ""} to ${item.endDate || ""}</div>
          <div class="box"><strong>Submitted</strong><br/>${submittedText}</div>
          <div class="box"><strong>Status</strong><br/>${item.status || "pending"}</div>
          <div class="box"><strong>Reason / note</strong><br/>${item.reason || ""}</div>
          <div class="box"><strong>Driver signature</strong><br/><br/>${signatureHtml}</div>
          <div class="box"><strong>Manager note</strong><br/>${item.managerNote || ""}</div>
        </body>
      </html>
    `);
    popup.document.close();
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading approvals...</div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-4 sm:space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 sm:px-6">
          <div className="flex items-baseline gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
              Admin
            </span>
            <h1 className="text-lg font-semibold tracking-tight text-gray-950">Timesheets &amp; leave requests</h1>
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-semibold text-gray-900">{timesheets.length}</span> timesheets
            {" · "}
            <span className="font-semibold text-gray-900">{leaveRequests.length}</span> leave
            {" · "}
            <span className="font-semibold text-amber-700">{needsApprovalCount}</span> need approval
          </div>
        </div>

        <div className="px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setActiveTab("timesheets")} className={`inline-flex items-center rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition ${activeTab === "timesheets" ? "bg-gray-900 text-white" : "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"}`}>
                <span>Timesheets</span>
                <NotificationPill count={timesheetSubmissionCount} active={activeTab === "timesheets"} />
              </button>
              <button onClick={() => setActiveTab("leave")} className={`inline-flex items-center rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition ${activeTab === "leave" ? "bg-gray-900 text-white" : "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"}`}>
                <span>Leave requests</span>
                <NotificationPill count={leaveSubmissionCount} active={activeTab === "leave"} />
              </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:min-w-[520px]">
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search driver, date, status..." className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100" />
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100">
                <option value="needsApproval">Needs approval</option>
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>
          {message && <div className="mt-4 rounded-2xl border border-emerald-100 bg-white/80 px-4 py-3 text-sm font-medium text-gray-700 shadow-sm">{message}</div>}
        </div>
      </div>

      {activeTab === "timesheets" ? (
        <div className="space-y-3">
          {visibleTimesheets.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">No timesheets found.</div>
          ) : (
            visibleTimesheets.map((item) => (
              <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-semibold text-gray-900">{item.driverName || item.driverEmail || "Driver"}</h2>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusBadgeClass(item.status)}`}>{item.status || "draft"}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">Week {item.weekStart} to {item.weekEnd} · {formatHours(item.totalHours)} hours</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PrintableButtons onPrint={() => openPrintableTimesheet(item)} />
                    <button onClick={() => approveOrRejectTimesheet(item, "approved")} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700">Approve</button>
                    <button onClick={() => approveOrRejectTimesheet(item, "rejected")} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700">Reject</button>
                  </div>
                </div>
                <textarea
                  value={notes[item.id] ?? item.managerNote ?? ""}
                  onChange={(event) => setNotes((current) => ({ ...current, [item.id]: event.target.value }))}
                  placeholder="Manager note"
                  className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                />
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleLeaves.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">No leave requests found.</div>
          ) : (
            visibleLeaves.map((item) => (
              <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-semibold text-gray-900">{item.driverName || item.driverEmail || "Driver"}</h2>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusBadgeClass(item.status)}`}>{item.status || "pending"}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{item.type} · {item.startDate} to {item.endDate}</p>
                    <p className="mt-1 text-xs font-medium text-gray-500">
                      Submitted: {getLeaveSubmittedText(item) || "Not available"}
                    </p>
                    {item.reason && <p className="mt-2 text-sm text-gray-700">{item.reason}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PrintableButtons onPrint={() => openPrintableLeave(item)} />
                    <button onClick={() => approveOrRejectLeave(item, "approved")} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700">Approve</button>
                    <button onClick={() => approveOrRejectLeave(item, "rejected")} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700">Reject</button>
                  </div>
                </div>
                <textarea
                  value={notes[item.id] ?? item.managerNote ?? ""}
                  onChange={(event) => setNotes((current) => ({ ...current, [item.id]: event.target.value }))}
                  placeholder="Manager note"
                  className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
